import { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import getHEOnboardingProfile, { HEOnboardingProfile } from '../../api/getHEOnboardingProfile';
import setHEOnboardingProfile from '../../api/setHEOnboardingProfile';
import { GO_TO_APPOINTMENTS, GO_TO_HE_DASHBOARD, GO_TO_HE_ONBOARDING } from '../../routes/paths';
import { getDocIds, getSelectedClinicId, getSingleDoctor } from '../../store/idbHandler';
import { FetchingLoader, Footer, Header } from './Utils';
import { captureError, captureErrorString } from '../captureError';
import { notification } from 'antd';
import { HEOnboardingState, heOnboardingStepsArray } from './HEOnboarding';
import { track } from '../../tracking';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../store/reducers';
import { Actions } from '../../store/actions';
import { fetchDoc } from '../../store/actions/login';
import { OneMgSvg } from '../../components/prescriptionPad/rightPanel/finishScreen/OrderMedicineUtils';
import fetchAllHEOrder from '../../api/fetchAllHEOrder';
import moment from 'moment';
import { ACTIVE_TAB, ActiveTab } from '../HEDashboard';

interface State {
    isFetchingProfile?: boolean;
    isPharmaEnabled?: boolean;
    isLabEnabled?: boolean;
    isActivated?: boolean;
    isSavingProfile?: boolean;
    isSavingChanges?: boolean;
    activeTab?: ActiveTab;
}

interface Props extends RouteComponentProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
}

class HomePage extends PureComponent<Props, State> {
    startDate = moment().subtract(30, 'days').startOf('day').toISOString();
    endDate = moment().endOf('day').toISOString();

    state: State = {
        isFetchingProfile: true,
    };
    currentIndex = 0;

    componentDidMount = async (): Promise<void> => {
        const { getHEOnboardingProfile, fetchAllHEOrder } = this;
        getHEOnboardingProfile();
        fetchAllHEOrder();
    };

    getHEOnboardingProfile = async (): Promise<void> => {
        try {
            const { success, profile, error = '' } = await getHEOnboardingProfile();
            if (success && profile) {
                const { isPharmaEnabled, isLabEnabled, isActivated } = profile;
                this.setState({ isPharmaEnabled, isLabEnabled, isActivated });
            } else {
                captureErrorString(error);
                track('ERROR', { on: 'getHEOnboardingProfile', what: error });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getHEOnboardingProfile',
                error: (error as Error).message,
            });
        } finally {
            this.setState({ isFetchingProfile: false });
        }
    };

    fetchAllHEOrder = async (): Promise<void> => {
        const { startDate, endDate } = this;
        const docIds = getDocIds().join(',');

        try {
            const { allOrders } = await fetchAllHEOrder(
                docIds,
                startDate,
                endDate,
                ACTIVE_TAB.MEDS,
            );
            const totalOrders = allOrders.filter((i) => i.status !== 'not ordered');
            if (totalOrders.length > 0) {
                this.setState({ activeTab: ACTIVE_TAB.MEDS });
                track('SHOW_MY_ORDERS_MOUNT', { with: ACTIVE_TAB.MEDS });
            } else {
                const { allOrders } = await fetchAllHEOrder(
                    docIds,
                    startDate,
                    endDate,
                    ACTIVE_TAB.LABS,
                );
                const totalOrders = allOrders.filter((i) => i.status !== 'not ordered');
                if (totalOrders.length > 0) {
                    this.setState({ activeTab: ACTIVE_TAB.LABS });
                    track('SHOW_MY_ORDERS_MOUNT', { with: ACTIVE_TAB.LABS });
                } else {
                    track('SHOW_MY_ORDERS_NO_MOUNT');
                }
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'fetchAllHEOrder',
                error: (error as Error).message,
            });
        }
    };

    saveDetailsHandler = async (lastStep: HEOnboardingState) => {
        const { isPharmaEnabled, isLabEnabled } = this.state;
        this.setState({
            isSavingProfile: true,
        });
        await setHEOnboardingProfile({ isPharmaEnabled, isLabEnabled, lastStep });
        this.setState({
            isSavingProfile: false,
        });
    };

    onClickHandler = async () => {
        const { history } = this.props;
        const { saveDetailsHandler, currentIndex } = this;

        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex + 1]);
            history.replace(GO_TO_HE_ONBOARDING(heOnboardingStepsArray[currentIndex + 1]));
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'setHEOnboardingProfile',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        }
    };

    saveAndExitHandler = async () => {
        const { history } = this.props;
        const { saveDetailsHandler, currentIndex } = this;

        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex]);
            history.replace(GO_TO_APPOINTMENTS(getSelectedClinicId() || ''));
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'setHEOnboardingProfile',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        }
    };

    exitHandler = () => {
        const { history } = this.props;
        history.replace(GO_TO_APPOINTMENTS(getSelectedClinicId() || ''));
    };

    onSaveChangesHandler = async () => {
        const { dispatch, history } = this.props;
        this.setState({
            isSavingChanges: true,
        });
        try {
            const { isPharmaEnabled, isLabEnabled } = this.state;
            const isActivated = isPharmaEnabled || isLabEnabled;
            const newPayload: HEOnboardingProfile = {
                isActivated,
                isPharmaEnabled,
                isLabEnabled,
                activateInProfile: true,
            };
            if (!isActivated) newPayload.lastStep = 'home';
            await setHEOnboardingProfile(newPayload);
            track('SET_HE_ONBOARDING_PROFILE', {
                isPharmaEnabled: String(isPharmaEnabled),
                isLabEnabled: String(isLabEnabled),
            });
            const docId = getSingleDoctor()?._id;
            docId &&
                dispatch(
                    fetchDoc(docId, () => {
                        isActivated
                            ? history.replace(GO_TO_HE_DASHBOARD('not-ordered'))
                            : this.exitHandler();
                    }),
                );
            notification.success({
                message: isActivated
                    ? 'Changes saved successfully!'
                    : 'Health exchange deactivated successfully!',
                duration: 5,
            });
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'activateInGenie',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error while saving',
                duration: 5,
            });
        } finally {
            this.setState({
                isSavingChanges: false,
            });
        }
    };

    onMyOrdersCTAClick = () => {
        const { history } = this.props;
        const { activeTab } = this.state;
        const { startDate, endDate } = this;
        history.push(GO_TO_HE_DASHBOARD('pending', startDate, endDate, true));
        track('ON_MY_ORDERS_CTA_CLICK', { activeTab });
    };

    render(): ReactNode {
        const {
            isFetchingProfile,
            isPharmaEnabled,
            isLabEnabled,
            isSavingProfile,
            isActivated,
            isSavingChanges,
            activeTab,
        } = this.state;
        const {
            onClickHandler,
            saveAndExitHandler,
            exitHandler,
            currentIndex,
            onSaveChangesHandler,
            onMyOrdersCTAClick,
        } = this;

        const disabled = (!isLabEnabled && !isPharmaEnabled) || isSavingProfile || isSavingChanges;
        const saveButtonText =
            !isLabEnabled && !isPharmaEnabled && !isFetchingProfile
                ? 'Disable Clinic Commerce'
                : 'Save Changes';

        return (
            <Fragment>
                <FetchingLoader isFetchingProfile={isFetchingProfile} />
                <div className="flex flex-col h-full w-full justify-center overflow-hidden">
                    <Header
                        saveAndExitHandler={saveAndExitHandler}
                        disabled={false}
                        isSavingProfile={isSavingProfile}
                        buttonText="Exit"
                        exitHandler={exitHandler}
                        onMyOrdersCTAClick={activeTab ? onMyOrdersCTAClick : undefined}
                    />

                    <div className="flex-1 grid grid-cols-3 overflow-auto">
                        <div className="flex flex-col col-span-2">
                            <p className="text-40 font-700 pl-48">
                                Join{' '}
                                <span className="text-darwin-accent-bright-blue-800">
                                    Clinic Commerce
                                </span>
                            </p>

                            <div
                                style={{
                                    background:
                                        'radial-gradient(54.25% 58.72% at 40.74% 93.45%, #608CFE 0%, #FFF 100%)',
                                }}
                                className="flex flex-col flex-1 justify-between"
                            >
                                <p className="text-darwin-neutral-900 text-16 pt-16 pl-48">
                                    Enable lab orders from your clinic.
                                </p>
                                <img
                                    src="/images/he-main-bg.png"
                                    alt="HE Home page"
                                    className="w-80p ml-36"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col justify-end items-end space-y-20 pr-48 pb-16">
                            <p className="w-320 font-700">
                                Select Services You Want To Avail From Below -
                            </p>

                            <div className="flex-1">
                                {/* <div
                                    className={`p-16 pb-36 border ${
                                        isPharmaEnabled
                                            ? 'bg-darwin-accent-symptoms-blue-100 border-darwin-accent-bright-blue-800'
                                            : 'border-darwin-neutral-200'
                                    } rounded-8 cursor-pointer hover:shadow-tight-3 hover:scale-103 transition-all transform-gpu duration-300 ease-in-out`}
                                    onClick={() =>
                                        this.setState({
                                            isPharmaEnabled: !isPharmaEnabled,
                                        })
                                    }
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-12">
                                            <div className="flex items-center justify-center w-28 h-28 rounded-full bg-darwin-accent-sulphur-green-500">
                                                <svg
                                                    className="w-14 h-14 fill-white"
                                                    viewBox="0 0 19 15"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M16.5312 5.46875C14.5625 3.53125 11.4062 3.53125 9.4375 5.46875C7.5 7.4375 7.5 10.5938 9.4375 12.5625C11.4062 14.5 14.5625 14.5 16.5312 12.5625C18.4688 10.5938 18.4688 7.4375 16.5312 5.46875ZM10.0312 7.125L14.875 11.9688C11.7188 13.9688 8.03125 10.2812 10.0312 7.125ZM15.9375 10.9062L11.0938 6.0625C14.25 4.0625 17.9688 7.75 15.9375 10.9062ZM3.5 0C1.5625 0 0 1.59375 0 3.5V10.4688C0 12.4062 1.5625 13.9375 3.5 13.9375C5.40625 13.9375 7 12.375 7 10.4688V3.5C7 1.59375 5.40625 0 3.5 0ZM5.5 7H1.5V3.5C1.5 0.84375 5.5 0.84375 5.5 3.5V7Z" />
                                                </svg>
                                            </div>
                                            <p className="text-24 font-700">Pharmacy</p>
                                        </div>

                                        <div
                                            className={`flex items-center space-x-8 px-8 py-5 border border-darwin-accent-bright-blue-800 ${
                                                isPharmaEnabled
                                                    ? 'bg-darwin-accent-bright-blue-800'
                                                    : ''
                                            } rounded-4`}
                                        >
                                            {isPharmaEnabled && (
                                                <div>
                                                    <svg
                                                        className="w-12 h-12 fill-white"
                                                        viewBox="0 0 12 13"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M6 12.5C2.67188 12.5 0 9.82812 0 6.5C0 3.19531 2.67188 0.5 6 0.5C9.30469 0.5 12 3.19531 12 6.5C12 9.82812 9.30469 12.5 6 12.5ZM8.64844 5.39844H8.625C8.85938 5.1875 8.85938 4.83594 8.625 4.60156C8.41406 4.39062 8.0625 4.39062 7.85156 4.60156L5.25 7.22656L4.14844 6.125C3.91406 5.89062 3.5625 5.89062 3.35156 6.125C3.11719 6.33594 3.11719 6.6875 3.35156 6.89844L4.85156 8.39844C5.0625 8.63281 5.41406 8.63281 5.64844 8.39844L8.64844 5.39844Z" />
                                                    </svg>
                                                </div>
                                            )}
                                            <p
                                                className={`text-12 ${
                                                    isPharmaEnabled
                                                        ? 'text-white'
                                                        : 'text-darwin-accent-bright-blue-800'
                                                }`}
                                            >
                                                {isPharmaEnabled ? 'Selected' : 'Select'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-64">
                                        <p>Earn upto</p>
                                        <div className="flex items-center space-x-10 text-darwin-accent-sulphur-green-500 font-700">
                                            <p className="text-51">25</p>
                                            <div className="text-18 space-y-2">
                                                <p>%</p>
                                                <p>order value</p>
                                            </div>
                                        </div>
                                    </div>

                                    <p className="pt-30 pb-24">
                                        Partner with a network of{' '}
                                        <span className="font-700">50k pharmacies </span>
                                        and get prescribed medicines ordered &amp; delivered to your
                                        patients.
                                    </p>

                                    <div className="space-y-10">
                                        <p className="text-darwin-neutral-600 text-11 uppercase text-center tracking-5">
                                            Available in
                                        </p>

                                        <div className="flex flex-wrap items-center justify-center space-x-8 font-700 text-13 text-darwin-neutral-800">
                                            <p>1000+ Cities with</p>
                                            <OneMgSvg width="100" height="30" />
                                        </div>
                                    </div>
                                </div> */}

                                <div
                                    className={`h-full w-320 p-16 pb-36 border ${
                                        isLabEnabled
                                            ? 'bg-darwin-accent-symptoms-blue-100 border-darwin-accent-bright-blue-800'
                                            : 'border-darwin-neutral-200'
                                    } rounded-8 cursor-pointer hover:shadow-tight-3 hover:scale-103 transition-all transform-gpu duration-300 ease-in-out`}
                                    onClick={() =>
                                        this.setState({
                                            isLabEnabled: !isLabEnabled,
                                        })
                                    }
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-12">
                                            <div className="flex items-center justify-center w-28 h-28 rounded-full bg-darwin-accent-bright-yellow-800">
                                                <svg
                                                    className="w-14 h-14 fill-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 512 512"
                                                >
                                                    <path d="M504.1 183l-176-176C324.3 2.344 318.1 0 312 0s-12.28 2.344-16.97 7.031c-9.375 9.375-9.375 24.56 0 33.94l15.16 15.16l-277.3 276.5c-38.75 38.75-45.13 102-9.375 143.5C44.08 500 72.76 512 101.5 512h.4473c26.38 0 52.75-9.1 72.88-30.12l281-280.1l15.19 15.19C475.7 221.7 481.8 224 488 224s12.28-2.344 16.97-7.031C514.3 207.6 514.3 192.4 504.1 183zM333.4 256H177.7l166.3-165.9l77.82 77.73L333.4 256z"></path>
                                                </svg>
                                            </div>
                                            <p className="text-24 font-700">Labs</p>
                                        </div>

                                        <div
                                            className={`flex items-center space-x-8 px-8 py-5 border border-darwin-accent-bright-blue-800 ${
                                                isLabEnabled
                                                    ? 'bg-darwin-accent-bright-blue-800'
                                                    : ''
                                            } rounded-4`}
                                        >
                                            {isLabEnabled && (
                                                <div>
                                                    <svg
                                                        className="w-12 h-12 fill-white"
                                                        viewBox="0 0 12 13"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M6 12.5C2.67188 12.5 0 9.82812 0 6.5C0 3.19531 2.67188 0.5 6 0.5C9.30469 0.5 12 3.19531 12 6.5C12 9.82812 9.30469 12.5 6 12.5ZM8.64844 5.39844H8.625C8.85938 5.1875 8.85938 4.83594 8.625 4.60156C8.41406 4.39062 8.0625 4.39062 7.85156 4.60156L5.25 7.22656L4.14844 6.125C3.91406 5.89062 3.5625 5.89062 3.35156 6.125C3.11719 6.33594 3.11719 6.6875 3.35156 6.89844L4.85156 8.39844C5.0625 8.63281 5.41406 8.63281 5.64844 8.39844L8.64844 5.39844Z" />
                                                    </svg>
                                                </div>
                                            )}
                                            <p
                                                className={`text-12 ${
                                                    isLabEnabled
                                                        ? 'text-white'
                                                        : 'text-darwin-accent-bright-blue-800'
                                                }`}
                                            >
                                                {isLabEnabled ? 'Selected' : 'Select'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-64">
                                        <p>Earn upto</p>
                                        <div className="flex items-center space-x-10 text-darwin-accent-sulphur-green-500 font-700">
                                            <p className="text-51">2L</p>
                                            <div className="text-18 space-y-2">
                                                <p>₹</p>
                                                <p>per month</p>
                                            </div>
                                        </div>
                                    </div>

                                    <p className="pt-30 pb-24">
                                        Offer a <span className="font-700">wide range</span> of labs
                                        to patients and increase the chance of them booking tests
                                        from your clinic.
                                    </p>

                                    <div className="space-y-8">
                                        <p className="text-darwin-neutral-600 text-11 uppercase text-center tracking-5">
                                            Our partners
                                        </p>

                                        <div className="flex items-center justify-center gap-x-16 space-y-8 flex-wrap font-700 text-13 text-darwin-neutral-800">
                                            <img
                                                src="/images/v_002.png"
                                                alt="lab 2"
                                                className="w-108 h-24"
                                            />
                                            <img
                                                src="/images/v_001.png"
                                                alt="lab 1"
                                                className="w-72 h-24"
                                            />
                                            <img
                                                src="/images/v_006.png"
                                                alt="lab 5"
                                                className="w-84 h-24"
                                            />
                                            <img
                                                src="/images/v_003.png"
                                                alt="lab 3"
                                                className="w-80 h-28"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Footer
                        onClickHandler={onClickHandler}
                        disabled={disabled}
                        isSavingProfile={isSavingProfile}
                        isSavingChanges={isSavingChanges}
                        buttonText={'Get Started'}
                        currentIndex={currentIndex}
                        onSaveChangesHandler={isActivated ? onSaveChangesHandler : undefined}
                        saveButtonText={isActivated ? saveButtonText : undefined}
                        isWarningBt={!isLabEnabled && !isPharmaEnabled}
                    />
                </div>
            </Fragment>
        );
    }
}

export default connect()(withRouter(HomePage));
